// src/components/courses/FoundationCourse.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaCheck, FaLock, FaChevronDown, FaChevronUp, FaVideo, FaFileAlt, FaFileDownload } from 'react-icons/fa';
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";

const FoundationCourse = () => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [activeModule, setActiveModule] = useState(0);
  const [activeLesson, setActiveLesson] = useState(0);
  const [expandedModules, setExpandedModules] = useState([0]);
  const [progress, setProgress] = useState({});
  const videoRef = useRef(null);

  useEffect(() => {
    const checkAccess = async () => {
      // If no user, check if course is free
      if (!user) {
        const { data: course } = await supabase
          .from("courses")
          .select("is_free")
          .eq("slug", "foundations")
          .single();

        setHasAccess(course?.is_free || false);
        return;
      }

      // 1️⃣ Check if course is free
      const { data: course } = await supabase
        .from("courses")
        .select("is_free")
        .eq("slug", "foundations")
        .single();

      if (course?.is_free) {
        setHasAccess(true);
        return;
      }

      // 2️⃣ Otherwise check explicit access
      const { data: access } = await supabase
        .from("course_access")
        .select("*")
        .eq("course_slug", "foundations")
        .eq("user_id", user.id)
        .single();

      setHasAccess(!!access);
    };

    checkAccess();
  }, [user]);


  // Course data structure
  const courseData = {
    title: "Data Foundations",
    description: "Master the essential skills and concepts required for any data career path",
    modules: [
      {
        id: "module-1",
        title: "Introduction to Data",
        description: "Understand what data is and why it matters in today's world",
        lessons: [
          {
            id: "lesson-1-1",
            title: "What is Data?",
            duration: "8:24",
            videoId: "aVw-Borz4fI", // Corrected YouTube video ID
            description: "An introduction to data concepts and their importance in decision-making",
            resources: [
              { name: "Lesson Slides", url: "/resources/slides-1-1.pdf", type: "pdf" },
              { name: "Practice Exercise", url: "/resources/exercise-1-1.xlsx", type: "excel" }
            ]
          },
          {
            id: "lesson-1-2",
            title: "Types of Data",
            duration: "12:15",
            videoId: "another-youtube-video-id", // Replace with your actual YouTube video ID
            description: "Learn about different data types and classifications",
            resources: [
              { name: "Lesson Slides", url: "/resources/slides-1-2.pdf", type: "pdf" },
              { name: "Reference Guide", url: "/resources/data-types-guide.pdf", type: "pdf" }
            ]
          }
          // Add more lessons as needed
        ]
      },
      {
        id: "module-2",
        title: "Data Collection Methods",
        description: "Explore various techniques for gathering quality data",
        locked: false, // Set to true if you want to lock this module initially
        lessons: [
          {
            id: "lesson-2-1",
            title: "Surveys and Questionnaires",
            duration: "15:30",
            videoId: "survey-video-id", // Replace with your actual YouTube video ID
            description: "Learn effective survey design principles and implementation",
            resources: [
              { name: "Survey Templates", url: "/resources/survey-templates.docx", type: "doc" },
              { name: "Case Study", url: "/resources/survey-case-study.pdf", type: "pdf" }
            ]
          }
          // Add more lessons as needed
        ]
      }
      // Add more modules as needed
    ]
  };

  // Load progress from localStorage on component mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('foundationCourseProgress');
    if (savedProgress) {
      setProgress(JSON.parse(savedProgress));
    }
  }, []);

  // Save progress to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('foundationCourseProgress', JSON.stringify(progress));
  }, [progress]);

  // Load progress from database for logged-in users
  useEffect(() => {
    const loadProgress = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from("lesson_progress")
        .select("lesson_id, completed")
        .eq("user_id", user.id)
        .eq("course_slug", "foundations");

      if (!error && data) {
        const progressMap = {};
        data.forEach(row => {
          progressMap[row.lesson_id] = { completed: row.completed };
        });
        setProgress(progressMap);
      }
    };

    loadProgress();
  }, [user]);

  // Handle module expansion toggle
  const toggleModule = (moduleIndex) => {
    if (expandedModules.includes(moduleIndex)) {
      setExpandedModules(expandedModules.filter(index => index !== moduleIndex));
    } else {
      setExpandedModules([...expandedModules, moduleIndex]);
    }
  };

  // Handle lesson selection
  const selectLesson = (moduleIndex, lessonIndex) => {
    // Don't allow selecting lessons in locked modules
    if (courseData.modules[moduleIndex].locked) return;
    
    setActiveModule(moduleIndex);
    setActiveLesson(lessonIndex);
    
    // Reset video time when changing lessons
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  // Mark lesson as completed
  const completeLesson = async () => {
    const lessonId = courseData.modules[activeModule].lessons[activeLesson].id;

    // Save to database if user is logged in
    if (user) {
      await supabase
        .from("lesson_progress")
        .upsert({
          user_id: user.id,
          course_slug: "foundations",
          lesson_id: lessonId,
          completed: true,
          completed_at: new Date().toISOString()
        });
    }

    // Update local state
    setProgress(prev => ({
      ...prev,
      [lessonId]: { completed: true }
    }));

    // Automatically advance to next lesson if available
    const currentModule = courseData.modules[activeModule];
    if (activeLesson < currentModule.lessons.length - 1) {
      // Next lesson in same module
      setActiveLesson(activeLesson + 1);
    } else if (activeModule < courseData.modules.length - 1) {
      // First lesson in next module
      if (!courseData.modules[activeModule + 1].locked) {
        setActiveModule(activeModule + 1);
        setActiveLesson(0);
        if (!expandedModules.includes(activeModule + 1)) {
          setExpandedModules([...expandedModules, activeModule + 1]);
        }
      }
    }
  };

  // Calculate module completion percentage
  const getModuleCompletionPercentage = (moduleIndex) => {
    const module = courseData.modules[moduleIndex];
    const completedLessons = module.lessons.filter(lesson => 
      progress[lesson.id]?.completed
    ).length;
    
    return (completedLessons / module.lessons.length) * 100;
  };

  // Check if a lesson is completed
  const isLessonCompleted = (lessonId) => {
    return progress[lessonId]?.completed || false;
  };

  // Get current lesson data
  const currentModule = courseData.modules[activeModule];
  const currentLesson = currentModule.lessons[activeLesson];

  // Access control checks
  if (hasAccess === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#f97316] mx-auto mb-4"></div>
          <p>Checking access…</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-900">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-2 text-gray-900">Access Denied</h2>
          <p className="text-gray-600 mb-6">You don't have access to this course.</p>
          <Link 
            to="/"
            className="px-6 py-3 rounded-md bg-[#f97316] hover:bg-[#ea580c] text-white transition-all duration-300 inline-block shadow-md hover:shadow-lg"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Course Header - Keep Dark */}
      <div className="bg-gradient-to-r from-primary-dark to-primary py-8 px-4 shadow-lg">
        <div className="max-w-[1240px] mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white">{courseData.title}</h1>
          <p className="text-gray-200 md:text-lg">{courseData.description}</p>
        </div>
      </div>

      <div className="max-w-[1240px] mx-auto px-4 py-8 flex flex-col lg:flex-row gap-8">
        {/* Sidebar with Course Navigation */}
        <div className="lg:w-1/4">
          <div className="bg-white rounded-lg p-4 shadow-lg sticky top-24 border border-gray-200">
            <h2 className="text-xl font-bold mb-4 pb-3 text-primary border-b border-gray-200">Course Content</h2>
            
            {courseData.modules.map((module, moduleIndex) => (
              <div key={module.id} className="mb-3">
                {/* Module Header */}
                <div 
                  className={`flex items-center justify-between p-3 rounded-md cursor-pointer ${
                    module.locked 
                      ? 'bg-gray-200 text-gray-500' 
                      : 'bg-white hover:bg-gray-50 border border-gray-200 transition-colors duration-200'
                  }`}
                  onClick={() => !module.locked && toggleModule(moduleIndex)}
                >
                  <div className="flex items-center">
                    {module.locked ? (
                      <FaLock className="mr-2 text-gray-500" size={14} />
                    ) : (
                      <div className="relative mr-3">
                        <div className="h-5 w-5 rounded-full border-2 border-[#f97316] flex items-center justify-center">
                          {getModuleCompletionPercentage(moduleIndex) === 100 ? (
                            <FaCheck size={10} className="text-[#f97316]" />
                          ) : null}
                        </div>
                        {getModuleCompletionPercentage(moduleIndex) > 0 && getModuleCompletionPercentage(moduleIndex) < 100 && (
                          <svg className="absolute top-0 left-0" width="20" height="20">
                            <circle 
                              cx="10" cy="10" r="8" 
                              fill="none" 
                              stroke="#f97316" 
                              strokeWidth="2" 
                              strokeDasharray={`${getModuleCompletionPercentage(moduleIndex) * 0.5} 50`} 
                              transform="rotate(-90 10 10)" 
                            />
                          </svg>
                        )}
                      </div>
                    )}
                    <span className="font-medium text-gray-800">{module.title}</span>
                  </div>
                  {!module.locked && (
                    expandedModules.includes(moduleIndex) 
                      ? <FaChevronUp size={14} /> 
                      : <FaChevronDown size={14} />
                  )}
                </div>
                
                {/* Module Lessons */}
                {!module.locked && expandedModules.includes(moduleIndex) && (
                  <div className="mt-1 ml-2 pl-2 border-l border-gray-300">
                    {module.lessons.map((lesson, lessonIndex) => (
                      <div 
                        key={lesson.id}
                        className={`flex items-center py-2 px-3 my-1 rounded-md cursor-pointer ${
                          activeModule === moduleIndex && activeLesson === lessonIndex 
                            ? 'bg-secondary text-white' 
                            : 'text-gray-700 hover:bg-gray-100'
                        } transition-colors duration-200`}
                        onClick={() => selectLesson(moduleIndex, lessonIndex)}
                      >
                        <div className="mr-3 w-5 h-5 flex-shrink-0">
                          {isLessonCompleted(lesson.id) ? (
                            <div className="h-5 w-5 rounded-full bg-[#f97316]/20 flex items-center justify-center">
                              <FaCheck size={10} className="text-[#f97316]" />
                            </div>
                          ) : (
                            <div className={`h-5 w-5 rounded-full flex items-center justify-center border-2 ${
                              activeModule === moduleIndex && activeLesson === lessonIndex 
                                ? 'border-white' 
                                : 'border-gray-500'
                            }`}>
                              <FaPlay size={8} className={`ml-0.5 ${
                                activeModule === moduleIndex && activeLesson === lessonIndex 
                                  ? 'text-white drop-shadow-md' 
                                  : 'text-gray-500'
                              }`} />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{lesson.title}</div>
                          <div className={`text-xs flex items-center mt-0.5 ${
                            activeModule === moduleIndex && activeLesson === lessonIndex 
                              ? 'text-white/90' 
                              : 'text-gray-500'
                          }`}>
                            <FaVideo className="mr-1" size={10} />
                            <span>{lesson.duration}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="lg:w-3/4">
          {/* Video Player Container */}
          <div className="bg-white rounded-lg overflow-hidden shadow-lg border border-gray-200">
            <div className="aspect-w-16 aspect-h-9 relative">
              <iframe 
                src={`https://www.youtube.com/embed/${currentLesson.videoId}`}
                title={currentLesson.title}
                className="w-full h-[500px]" // Increased height for better visibility
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            
            {/* Video Controls */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">{currentLesson.title}</h2>
                <button 
                  onClick={completeLesson}
                  disabled={isLessonCompleted(currentLesson.id)}
                  className={`px-4 py-2 rounded-md flex items-center ${
                    isLessonCompleted(currentLesson.id)
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-[#f97316] hover:bg-[#ea580c] text-white transition-colors duration-200'
                  }`}
                >
                  {isLessonCompleted(currentLesson.id) ? (
                    <>
                      <FaCheck className="mr-2" />
                      <span>Completed</span>
                    </>
                  ) : (
                    <>
                      <FaCheck className="mr-2" />
                      <span>Mark as Complete</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Navigation Buttons */}
              <div className="flex gap-3 mt-4">
                <button 
                  onClick={() => {
                    if (activeLesson > 0) {
                      // Previous lesson in same module
                      selectLesson(activeModule, activeLesson - 1);
                    } else if (activeModule > 0) {
                      // Last lesson in previous module
                      const prevModule = courseData.modules[activeModule - 1];
                      if (!prevModule.locked) {
                        selectLesson(activeModule - 1, prevModule.lessons.length - 1);
                      }
                    }
                  }}
                  disabled={activeModule === 0 && activeLesson === 0}
                  className={`px-4 py-2 rounded-md flex-1 ${
                    activeModule === 0 && activeLesson === 0
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 transition-colors duration-200'
                  }`}
                >
                  Previous Lesson
                </button>
                <button 
                  onClick={() => {
                    if (activeLesson < currentModule.lessons.length - 1) {
                      // Next lesson in same module
                      selectLesson(activeModule, activeLesson + 1);
                    } else if (activeModule < courseData.modules.length - 1) {
                      // First lesson in next module
                      const nextModule = courseData.modules[activeModule + 1];
                      if (!nextModule.locked) {
                        selectLesson(activeModule + 1, 0);
                      }
                    }
                  }}
                  disabled={
                    activeModule === courseData.modules.length - 1 && 
                    activeLesson === currentModule.lessons.length - 1
                  }
                  className={`px-4 py-2 rounded-md flex-1 ${
                    activeModule === courseData.modules.length - 1 && 
                    activeLesson === currentModule.lessons.length - 1
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-[#f97316] hover:bg-[#ea580c] text-white transition-colors duration-200'
                  }`}
                >
                  Next Lesson
                </button>
              </div>
            </div>
          </div>
          
          {/* Lesson Content and Resources */}
          <div className="mt-8 bg-white rounded-lg p-6 shadow-lg border border-gray-200">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{currentLesson.title}</h3>
            <p className="text-gray-600 mb-6">{currentLesson.description}</p>
            
            {/* Resources Section */}
            {currentLesson.resources && currentLesson.resources.length > 0 && (
              <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4 text-gray-900 flex items-center">
                  <FaFileAlt className="mr-2 text-[#f97316]" />
                  Lesson Resources
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentLesson.resources.map((resource, index) => (
                    <a 
                      key={index}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-all duration-300 group"
                    >
                      <div className="w-10 h-10 bg-[#f97316]/20 rounded-full flex items-center justify-center mr-4 group-hover:bg-[#f97316]/30 transition-colors duration-300">
                        <FaFileDownload className="text-[#f97316]" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{resource.name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {resource.type.toUpperCase()}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoundationCourse;